import { ApiError, request } from './api-client';

/**
 * Client for the clinical and business modules.
 *
 * Split from the core client purely so each file stays readable; both are
 * merged into the single `api` object that pages import.
 */

/** A line (two points) or an angle (three; the angle is at the second), in image pixels. */
export interface ImageMeasurement {
  id: string;
  kind: 'length' | 'angle';
  points: [number, number][];
  label?: string;
}
/** What the viewer keeps with an image: scale, window, measurements. */
export interface ImageAnnotations {
  version?: 1;
  calibration?: { pxPerMm: number; method: 'known_length' | 'pixel_size'; note?: string } | null;
  adjustments?: {
    brightness?: number;
    contrast?: number;
    gamma?: number;
    invert?: boolean;
    rotate?: 0 | 90 | 180 | 270;
    flipH?: boolean;
    flipV?: boolean;
  };
  measurements?: ImageMeasurement[];
}

export interface ChartData {
  patient: {
    id: string;
    birthDate: string | null;
    givenName: string;
    familyName: string;
    givenNameAlt: string | null;
    familyNameAlt: string | null;
  };
  teeth: { toothFdi: string; status: string; kind: string; note: string | null; effectiveAt: string }[];
  surfaces: { toothFdi: string; surface: string; finding: string; effectiveAt: string }[];
  treatments: TreatmentView[];
  images: {
    linkId: string;
    attachmentId: string;
    toothFdi: string | null;
    surfaces: string[];
    kind: string;
    isSynthetic: boolean;
    at: string;
  }[];
  history: {
    observations: { id: string; toothFdi: string; status: string; note: string | null; effectiveAt: string }[];
    surfaces: { id: string; toothFdi: string; surface: string; finding: string; effectiveAt: string }[];
  };
}

/**
 * The outcome of asking to remove something.
 *
 * Two booleans rather than one, because "deleted" and "deactivated" are
 * genuinely different outcomes and the server chooses between them based on
 * whether history references the record. `reason` names what forced the
 * choice, e.g. `{ purchases: 4 }`.
 */
export interface Removal {
  deleted: boolean;
  deactivated: boolean;
  reason?: Record<string, number>;
}

export interface PaymentFilters {
  q?: string;
  direction?: 'incoming' | 'outgoing';
  from?: string;
  to?: string;
  includeReversed?: boolean;
}

function paymentQuery(patientId: string | undefined, f: PaymentFilters): string {
  const q = new URLSearchParams();
  if (patientId) q.set('patientId', patientId);
  if (f.q) q.set('q', f.q);
  if (f.direction) q.set('direction', f.direction);
  if (f.from) q.set('from', f.from);
  if (f.to) q.set('to', f.to);
  if (f.includeReversed === false) q.set('includeReversed', 'false');
  return q.toString();
}

export interface Payables {
  suppliers: {
    id: string;
    name: string;
    currency: string;
    billedMinor: number;
    paidMinor: number;
    outstandingMinor: number;
    openBills: number;
  }[];
  laboratories: {
    id: string;
    name: string;
    currency: string;
    billedMinor: number;
    paidMinor: number;
    outstandingMinor: number;
    pendingMinor: number;
    receivedOrders: number;
  }[];
  totals: {
    suppliersOutstandingMinor: number;
    laboratoriesOutstandingMinor: number;
    laboratoriesPendingMinor: number;
  };
}

export interface ExportOption {
  kind: string;
  needs: string[];
  available: boolean;
  money: boolean;
  takesRange: boolean;
  takesDentist: boolean;
}

export interface ImportReport {
  total: number;
  ready: number;
  duplicates: number;
  errors: number;
  rows: {
    line: number;
    status: 'ready' | 'duplicate' | 'error';
    message: string | null;
    name: string | null;
    phone: string | null;
    existing: { id: string; patientNumber: string; name: string } | null;
  }[];
}

export interface TreatmentView {
  id: string;
  patientId: string;
  nameEn: string;
  nameAr: string;
  toothFdi: string | null;
  surfaces: string[];
  /** Derma and aesthetic work: the zone instead of a tooth, with its dose, product and lot. */
  zone: string | null;
  doseQty: number | null;
  doseUnit: string | null;
  productLabel: string | null;
  lotNumber: string | null;
  status: string;
  priceMinor: number;
  currency: string;
  billable: boolean;
  billed: boolean;
  completedAt: string | null;
  createdAt: string;
  patient?: { patientNumber: string; givenName: string; familyName: string };
  dentistMembershipId: string | null;
  notes: string | null;
}

export interface CatalogueItem {
  id: string;
  code: string;
  nameEn: string;
  nameAr: string;
  category: string;
  priceMinor: number;
  currency: string;
  durationMinutes: number;
  requiresTooth: boolean;
  impliesLab: boolean;
  /** What the tooth becomes once this is done. Null = changes nothing. */
  resultingToothStatus: string | null;
  /** 'dental' | 'dermatology' | 'aesthetics' | 'general'. */
  specialty: string;
  /** Recorded on a face or body zone instead of a tooth. */
  requiresZone: boolean;
  /** How its dose is counted ('units', 'ml', 'shots'…), or null. */
  doseUnit: string | null;
  /** The mark a completed one leaves on the zone, or null. */
  zoneMark: string | null;
}

/** The face and body chart of one patient. */
export interface ZoneChartData {
  zones: { zone: string; region: 'face' | 'body'; finding: string | null; severity: string | null; kind: string; note: string | null; effectiveAt: string }[];
  procedures: {
    id: string;
    zone: string;
    region: string;
    mark: string | null;
    detail: Record<string, unknown>;
    note: string | null;
    treatmentId: string | null;
    effectiveAt: string;
  }[];
  treatments: TreatmentView[];
  images: { linkId: string; attachmentId: string; zone: string | null; kind: string; mimeType: string; isSynthetic: boolean; at: string }[];
  history: {
    id: string;
    zone: string;
    region: string;
    kind: string;
    finding: string | null;
    severity: string | null;
    mark: string | null;
    detail: Record<string, unknown>;
    note: string | null;
    supersedesId: string | null;
    effectiveAt: string;
  }[];
}

/** A ready-to-send message. Opening it is the ONLY thing that happens. */
export interface WhatsAppDraft {
  kind: string;
  phone: string;
  message: string;
  url: string;
  preview: { recipient: string; phoneDisplay: string; lines: string[] };
}

export interface InvoiceRow {
  id: string;
  number: string | null;
  /** The reference an offline device showed before the server numbered it. */
  provisionalRef?: string | null;
  status: string;
  currency: string;
  issuedAt: string | null;
  dueAt: string | null;
  totalMinor: number;
  paidMinor: number;
  outstandingMinor: number;
  patient: {
    id: string;
    patientNumber: string;
    givenName: string;
    familyName: string;
    givenNameAlt: string | null;
    familyNameAlt: string | null;
  };
}

export interface MaterialRow {
  id: string;
  sku: string;
  nameEn: string;
  nameAr: string;
  category: string;
  baseUnit: string;
  packUnit: string;
  packSize: number;
  reorderThreshold: number;
  location: string | null;
  onHand: number;
  level: string;
  unitCostMinor: number;
  valueMinor: number;
  preferredSupplier: { id: string; name: string } | null;
  lastSupplier: { id: string; name: string } | null;
  /** false means retired: kept for its stock history, out of the pickers. */
  active: boolean;
}

export interface LabOrderRow {
  id: string;
  itemType: string;
  /** The planned treatment this order was raised from, when it came from one. */
  treatmentId: string | null;
  teeth: string[];
  shade: string | null;
  status: string;
  dueOn: string | null;
  overdue: boolean;
  labCostMinor: number;
  patientChargeMinor: number;
  currency: string;
  laboratory: { id: string; name: string; whatsappPhone: string | null };
  patient: {
    id: string;
    patientNumber: string;
    givenName: string;
    familyName: string;
    givenNameAlt: string | null;
    familyNameAlt: string | null;
  };
  allowedNext: string[];
  /** Every state, for a correction that goes outside the workflow. */
  allStates: readonly string[];
  specification: string | null;
  instructions: string | null;
  laboratoryId: string;
  /** The clinic currently owes the lab for this order. */
  payableRecorded: boolean;
}

export interface FinanceReport {
  period: { from: string; to: string };
  currency: string;
  figures: {
    billedMinor: number;
    collectedMinor: number;
    outstandingMinor: number;
    expensesMinor: number;
    labCostMinor: number;
    commissionMinor: number;
  };
  aging: Record<string, number>;
  byDentist: {
    membershipId: string;
    name: string;
    uniquePatients: number;
    uniqueTeeth: number;
    procedures: number;
    producedMinor: number;
  }[];
  byCategory: { category: string; procedures: number; producedMinor: number }[];
  monthly: { month: string; collectedMinor: number; paidOutMinor: number }[];
  paidOut: {
    dentists: { membershipId: string; name: string; amountMinor: number }[];
    suppliers: { id: string; name: string; amountMinor: number }[];
    laboratories: { id: string; name: string; amountMinor: number }[];
    patientRefundsMinor: number;
    otherMinor: number;
    expensesByCategory: { category: string; amountMinor: number }[];
  };
}

export interface DentistStatement {
  dentist: { membershipId: string; name: string };
  period: { from: string; to: string };
  currency: string;
  production: { procedures: number; uniquePatients: number; uniqueTeeth: number; producedMinor: number };
  commission: {
    accruedMinor: number;
    settledMinor: number;
    outstandingMinor: number;
    basis: string | null;
    rateBp: number | null;
  };
  /** A salary is a separate agreement that runs alongside any commission. */
  salary: {
    monthlyMinor: number;
    accruedMinor: number;
    unposted: { month: string; amountMinor: number; coveredDays: number; monthDays: number }[];
  };
  accruals: {
    id: string;
    amountMinor: number;
    rateBp: number;
    basis: string;
    sourceType: string;
    at: string;
    /** Paid so far against this one earning. Partial payment is normal. */
    allocatedMinor: number;
    settled: boolean;
  }[];
  /** Present for the owner (profit basis): what there is to draw on. */
  owner: {
    period: { from: string; to: string };
    collectedMinor: number;
    expensesMinor: number;
    labCostMinor: number;
    purchasesMinor: number;
    othersCommissionMinor: number;
    profitMinor: number;
    drawnMinor: number;
    availableMinor: number;
  } | null;
  settlements: {
    id: string;
    periodStart: string;
    periodEnd: string;
    amountMinor: number;
    status: string;
    kind: string;
    method: string;
    memo: string | null;
    settledAt: string | null;
  }[];
  agreements: {
    id: string;
    basis: string;
    rateBp: number;
    fixedMinor: number;
    monthlySalaryMinor: number;
    currency: string;
    deductLabCost: boolean;
    effectiveFrom: string;
    effectiveTo: string | null;
  }[];
}

/** Produced, earned and paid for each month separately — never one divided. */
export interface DentistMonthly {
  currency: string;
  months: {
    month: string;
    procedures: number;
    uniquePatients: number;
    uniqueTeeth: number;
    producedMinor: number;
    commissionMinor: number;
    salaryMinor: number;
    paidMinor: number;
  }[];
}

/** Utilisation by procedure code over a month / quarter / half / year. */
export interface ProcedureReport {
  period: { grain: string; from: string; to: string; key: string };
  currency: string;
  totals: { performed: number; billedMinor: number; distinctCodes: number; uncoded: number };
  procedures: {
    code: string | null;
    nameEn: string | null;
    nameAr: string | null;
    category: string | null;
    performed: number;
    uniquePatients: number;
    dentists: number;
    billedMinor: number;
  }[];
  byValue: ProcedureReport['procedures'];
  byDentist: {
    membershipId: string;
    name: string;
    performed: number;
    billedMinor: number;
    distinctProcedures: number;
  }[];
}

export interface PlatformClinicRow {
  id: string;
  slug: string;
  nameEn: string;
  nameAr: string;
  status: string;
  timezone: string;
  currency: string;
  ownerEmail: string | null;
  plan: string | null;
  subscriptionStatus: string | null;
  startsAt: string | null;
  endsAt: string | null;
  expired: boolean;
  expiringSoon: boolean;
  daysLeft: number | null;
  paused: boolean;
  pausedUntil: string | null;
  pauseReason: string | null;
  /** The clinic's licence code, readable so it can be sent again. */
  activationCode: string | null;
  activeMembers: number;
  dentists: number;
  seatLimit: number | null;
  dentistLimit: number | null;
  patients: number;
  createdAt: string;
}

export interface PlatformOverview {
  clinics: PlatformClinicRow[];
  totals: { clinics: number; active: number; suspended: number; expired: number; expiringSoon: number; paused: number };
  attention: {
    id: string;
    nameEn: string;
    nameAr: string;
    ownerEmail: string | null;
    expired: boolean;
    expiringSoon: boolean;
    paused: boolean;
    daysLeft: number | null;
    endsAt: string | null;
    pausedUntil: string | null;
  }[];
  plans: Record<string, { plan: string; features: string[]; limits: Record<string, number | null> }>;
}

export interface PlatformClinicDetail {
  clinic: {
    id: string;
    slug: string;
    nameEn: string;
    nameAr: string;
    email: string | null;
    status: string;
    timezone: string;
    currency: string;
    createdAt: string;
    paused: boolean;
    pausedUntil: string | null;
    pauseReason: string | null;
    patients: number;
    policyAcceptedAt: string | null;
    policyVersion: string | null;
  };
  subscription: { plan: string; status: string; startsAt: string; endsAt: string | null; maxDentists: number | null; maxActiveUsers: number | null } | null;
  activationCode: { code: string | null; email: string; usedTimes: number; issuedAt: string } | null;
  members: {
    membershipId: string;
    name: string;
    email: string;
    roles: string[];
    status: string;
    isDentist: boolean;
    activatedAt: string | null;
    invitedAt: string | null;
    isOwner: boolean;
  }[];
}

export interface ActivationCode {
  id: string;
  /** All that survives of the code: it is stored only as a hash. */
  prefix: string;
  plan: string;
  email: string;
  clinicId: string | null;
  clinicName: string | null;
  redemptions: number;
  maxRedemptions: number;
  expiresAt: string;
  revokedAt: string | null;
  state: 'available' | 'redeemed' | 'expired' | 'revoked';
  createdAt: string;
}

export interface SupportGrant {
  id: string;
  clinicId: string;
  clinicName: string;
  operatorEmail: string;
  reason: string;
  scope: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  active: boolean;
}

export interface PrescriptionDetail {
  prescription: {
    id: string;
    patientId: string;
    state: string;
    issuedAt: string | null;
    prescriber: string | null;
  };
  items: {
    id: string;
    medicineText: string;
    ingredients: string[];
    dose: string;
    route: string;
    frequency: string;
    duration: string;
    quantity: string | null;
    instructions: string | null;
  }[];
  check: {
    status: string;
    findings: { kind: string; severity: string; message: string }[];
    source: string;
    isSynthetic: boolean;
    checkedAt: string;
    stale: boolean;
  } | null;
  issuable: { allowed: boolean; reason?: string };
  pdf: string;
  mode: { mode: string; clinical: boolean; warning: string | null };
}


export interface PatientSource {
  id: string;
  nameEn: string;
  nameAr: string | null;
  active: boolean;
  sort: number;
  patients?: number;
}

export interface JourneyNote {
  id: string;
  body: string;
  nextPlan: string | null;
  visibility: 'private' | 'shared';
  own: boolean;
  author: string | null;
  finalised: boolean;
  at: string;
}
export interface JourneyEntry {
  key: string;
  appointmentId: string | null;
  visitIds: string[];
  at: string;
  day: string;
  status: string;
  reason: string | null;
  dentist: { membershipId: string | null; name: string | null };
  followup: string | null;
  done: { id: string; nameEn: string; nameAr: string | null; tooth: string | null; zone: string | null }[];
  notes: JourneyNote[];
}
export interface Journey {
  upcoming: { id: string; at: string; reason: string | null; status: string; dentist: string | null }[];
  plan: { id: string; nameEn: string; nameAr: string | null; tooth: string | null; zone: string | null; status: string; dentist: string | null }[];
  nextPlan: string | null;
  entries: JourneyEntry[];
  notesHidden: boolean;
}

export interface MissedAppointment {
  id: string;
  startsAt: string;
  reason: string | null;
  dentist: { membershipId: string; name: string };
  patient: {
    id: string;
    patientNumber: string;
    givenName: string;
    familyName: string;
    givenNameAlt: string | null;
    familyNameAlt: string | null;
    phone: string | null;
    whatsappPhone: string | null;
  } | null;
  requestedBy: { name: string | null; phone: string | null } | null;
  followup: { status: 'contacted' | 'rebooked' | 'closed'; at: string; by: string | null; note: string | null } | null;
  nextBooked: string | null;
  open: boolean;
}

export interface ReportPeriod {
  from: string;
  to: string;
  dentistId?: string;
}
const periodQs = (p: ReportPeriod) => new URLSearchParams({ from: p.from, to: p.to, ...(p.dentistId ? { dentistId: p.dentistId } : {}) }).toString();

export const moduleApi = {
  // ------------------------------------------------------------- clinical
  catalogue: (specialty?: string) =>
    request<{ items: CatalogueItem[] }>(`/api/clinical/catalogue${specialty ? `?specialty=${encodeURIComponent(specialty)}` : ''}`),

  updateCatalogueItem: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/clinic/catalogue/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  installStandardCatalogue: () =>
    request<{ added: number }>('/api/clinic/catalogue/install-standard', { method: 'POST' }),

  chart: (patientId: string) => request<ChartData>(`/api/clinical/chart/${patientId}`),

  // The face and body chart (dermatology and aesthetics).
  zones: (patientId: string) => request<ZoneChartData>(`/api/clinical/zones/${patientId}`),

  recordZoneObservation: (patientId: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/clinical/zones/${patientId}/observation`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  recordObservation: (patientId: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/clinical/chart/${patientId}/observation`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  recordSurfaces: (patientId: string, body: Record<string, unknown>) =>
    request<{ ids: string[] }>(`/api/clinical/chart/${patientId}/surfaces`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  treatments: (patientId?: string) =>
    request<{ treatments: TreatmentView[] }>(
      `/api/clinical/treatments${patientId ? `?patientId=${patientId}` : ''}`,
    ),

  createTreatment: (body: Record<string, unknown>) =>
    request<{ treatment: TreatmentView; toothChartedAs: string | null }>('/api/clinical/treatments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  setTreatmentStatus: (id: string, body: Record<string, unknown>) =>
    request<{ treatment: TreatmentView; toothChartedAs: string | null }>(
      `/api/clinical/treatments/${id}/status`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  notes: (patientId: string) =>
    request<{
      notes: {
        id: string;
        body: string;
        toothFdi: string | null;
        finalised: boolean;
        author: string | null;
        occurredAt: string;
        amendments: { id: string; body: string; reason: string; author: string | null; at: string }[];
      }[];
    }>(`/api/clinical/notes/${patientId}`),

  createNote: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/clinical/notes', { method: 'POST', body: JSON.stringify(body) }),

  amendNote: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/clinical/notes/${id}/amend`, { method: 'POST', body: JSON.stringify(body) }),

  timeline: (patientId: string) =>
    request<{
      events: { at: string; kind: string; title: string; detail: string | null; actor: string | null }[];
      filtered: { finance: boolean; notes: boolean };
    }>(`/api/clinical/timeline/${patientId}`),

  // -------------------------------------------------------------- imaging
  /** What the viewer keeps with an image (`attachment.annotations`). */
  imageAnnotations: (id: string) => request<{ annotations: ImageAnnotations }>(`/api/imaging/${id}/annotations`),
  saveImageAnnotations: (id: string, body: ImageAnnotations) =>
    request<{ annotations: ImageAnnotations }>(`/api/imaging/${id}/annotations`, { method: 'PUT', body: JSON.stringify(body) }),

  imagingLibrary: (patientId: string) =>
    request<{
      images: {
        id: string;
        kind: string;
        mimeType: string;
        byteSize: number;
        source: string;
        isSynthetic: boolean;
        scanState: string;
        at: string;
        annotations: ImageAnnotations;
        links: { linkId: string; toothFdi: string | null; zone: string | null; surfaces: string[] }[];
      }[];
    }>(`/api/imaging/patients/${patientId}`),

  simulateCapture: (body: Record<string, unknown>) =>
    request<{ id: string; isSynthetic: boolean; warning: string }>('/api/imaging/simulate-capture', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadImage: (body: Record<string, unknown>) =>
    request<{ id: string; viewerSupported: boolean; viewerNote: string | null }>('/api/imaging/upload', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ------------------------------------------------------------- offline
  /** What the rules decided while devices were offline (ADR-0009). */
  syncNotices: (all = false) => request<{ notices: unknown[]; open: number; attention: number; cached?: boolean }>(`/api/sync/notices${all ? '?all=true' : ''}`),
  noticeSummary: () => request<{ open: number; attention: number }>('/api/sync/notices/summary'),
  ackNotice: (id: string) => request<{ id: string; acknowledged: boolean }>(`/api/sync/notices/${id}/ack`, { method: 'POST' }),

  // --------------------------------------------------------- prescriptions
  rxMode: () => request<{ mode: string; clinical: boolean; warning: string | null }>('/api/prescriptions/mode'),

  drugs: (q?: string) =>
    request<{
      drugs: {
        id: string;
        code: string;
        nameEn: string;
        nameAr: string;
        ingredients: string[];
        form: string | null;
        strength: string | null;
        isSynthetic: boolean;
      }[];
    }>(`/api/prescriptions/drugs${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  rxTemplates: () =>
    request<{
      templates: {
        id: string;
        nameEn: string;
        nameAr: string;
        description: string | null;
        items: {
          medicineText: string;
          dose: string;
          route: string;
          frequency: string;
          duration: string;
          instructions?: string;
        }[];
        /** 'clinic' = everyone's. 'personal' = this dentist's own. */
        scope: 'clinic' | 'personal';
        editable: boolean;
      }[];
    }>('/api/prescriptions/templates'),

  saveTemplate: (body: Record<string, unknown>) =>
    request<{ id: string; scope: string }>('/api/prescriptions/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateTemplate: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/prescriptions/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  prescriptions: (patientId?: string) =>
    request<{
      prescriptions: {
        id: string;
        state: string;
        issuedAt: string | null;
        createdAt: string;
        prescriber: string | null;
        itemCount: number;
        patient: {
          id: string;
          patientNumber: string;
          givenName: string;
          familyName: string;
          givenNameAlt: string | null;
          familyNameAlt: string | null;
        };
      }[];
    }>(`/api/prescriptions${patientId ? `?patientId=${patientId}` : ''}`),

  prescription: (id: string) => request<PrescriptionDetail>(`/api/prescriptions/${id}`),

  createPrescription: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/prescriptions', { method: 'POST', body: JSON.stringify(body) }),

  /** Rewrite an unissued prescription. Any previous safety verdict is void. */
  createRxTemplate: (body: Record<string, unknown>) =>
    request<{ id: string; scope: string }>('/api/prescriptions/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateRxTemplate: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/prescriptions/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  updatePrescription: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; state: string; mustRecheck: boolean }>(`/api/prescriptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  discardPrescription: (id: string, reason?: string) =>
    request<{ id: string; state: string }>(`/api/prescriptions/${id}/discard`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  cancelPrescription: (id: string, reason: string) =>
    request<{ id: string; state: string }>(`/api/prescriptions/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  checkPrescription: (id: string) =>
    request<{
      status: string;
      findings: { kind: string; severity: string; message: string }[];
      isSynthetic: boolean;
    }>(`/api/prescriptions/${id}/check`, { method: 'POST' }),

  issuePrescription: (id: string) =>
    request<{ id: string; state: string }>(`/api/prescriptions/${id}/issue`, { method: 'POST' }),

  // -------------------------------------------------------------- finance
  invoices: (patientId?: string) =>
    request<{ invoices: InvoiceRow[] }>(
      `/api/finance/invoices${patientId ? `?patientId=${patientId}` : ''}`,
    ),

  createInvoice: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/finance/invoices', { method: 'POST', body: JSON.stringify(body) }),

  postInvoice: (id: string) =>
    request<{ id: string; number: string | null; alreadyPosted: boolean }>(
      `/api/finance/invoices/${id}/post`,
      { method: 'POST' },
    ),

  updateTreatment: (id: string, body: Record<string, unknown>) =>
    request<{ treatment: TreatmentView; updated: boolean }>(`/api/clinical/treatments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteTreatment: (id: string, reason?: string) =>
    request<{ deleted: boolean; chartRestoredTo: string | null }>(`/api/clinical/treatments/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  paymentsList: (patientId?: string, filters: PaymentFilters = {}) =>
    request<{
      payments: {
        id: string;
        direction: string;
        amountMinor: number;
        currency: string;
        method: string;
        reference: string | null;
        category: string | null;
        receivedAt: string;
        /** This row IS a reversal of an earlier payment. */
        isReversal: boolean;
        /** This row HAS been reversed by a later one. */
        wasReversed: boolean;
        reversalReason: string | null;
        patient: { patientNumber: string; givenName: string; familyName: string } | null;
        /** Who the money went to or came from, by name. */
        counterparty: { kind: 'patient' | 'dentist' | 'supplier' | 'laboratory'; name: string } | null;
      }[];
    }>(`/api/finance/payments?${paymentQuery(patientId, filters)}`),

  recordPayment: (body: Record<string, unknown>) =>
    request<{
      id: string;
      duplicate: boolean;
      allocations: { invoiceId: string; amountMinor: number }[];
    }>('/api/finance/payments', { method: 'POST', body: JSON.stringify(body) }),

  expenses: () =>
    request<{
      expenses: {
        id: string;
        category: string;
        description: string;
        amountMinor: number;
        currency: string;
        payee: string | null;
        incurredOn: string;
      }[];
    }>('/api/finance/expenses'),

  recordExpense: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/finance/expenses', { method: 'POST', body: JSON.stringify(body) }),

  procedureReport: (params: { grain?: string; from?: string; to?: string; dentist?: string } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    return request<ProcedureReport>(`/api/finance/reports/procedures?${q.toString()}`);
  },

  financeReports: (from?: string, to?: string) =>
    request<FinanceReport>(`/api/finance/reports${from ? `?from=${from}&to=${to ?? ''}` : ''}`),

  patientAccount: (patientId: string) =>
    request<{
      currency: string;
      /** Net of unapplied credit; negative means the clinic holds the patient's money. */
      outstandingMinor: number;
      /** Paid but not yet applied to any invoice. */
      creditMinor: number;
      invoices: {
        id: string;
        number: string | null;
        status: string;
        issuedAt: string | null;
        totalMinor: number;
        paidMinor: number;
        outstandingMinor: number;
      }[];
      payments: { id: string; amountMinor: number; currency: string; method: string; at: string }[];
      /** Completed, chargeable, not yet on any invoice. */
      unbilled: {
        count: number;
        totalMinor: number;
        items: {
          id: string;
          nameEn: string;
          nameAr: string;
          toothFdi: string | null;
          priceMinor: number;
          currency: string;
          performedAt: string;
        }[];
      };
    }>(`/api/finance/patients/${patientId}/account`),

  // ------------------------------------------------------------- dentists
  dentists: () =>
    request<{
      dentists: {
        membershipId: string;
        name: string;
        email: string | null;
        signIn: boolean;
        roles: string[];
        assignedPatients: number;
        completedProcedures: number;
      }[];
    }>('/api/dentists'),

  /** A dentist who will not sign in (see DentistsController.addWithoutSignIn). */
  addDentist: (body: { fullName: string; publicTitle?: string | null }) =>
    request<{ membershipId: string; name: string }>('/api/dentists', { method: 'POST', body: JSON.stringify(body) }),

  dentistHours: (membershipId: string) =>
    request<{ hours: { weekday: number; start: string; end: string }[] }>(`/api/dentists/${membershipId}/hours`),

  setDentistHours: (membershipId: string, hours: { weekday: number; start: string; end: string }[]) =>
    request<{ hours: { weekday: number; start: string; end: string }[] }>(`/api/dentists/${membershipId}/hours`, { method: 'PUT', body: JSON.stringify({ hours }) }),

  dentistMonthly: (membershipId: string, months = 12) =>
    request<DentistMonthly>(`/api/dentists/${membershipId}/monthly?months=${months}`),

  /** Reverse a dentist payment made in error: reopens what it paid, returns the cash to the books. */
  reverseSettlement: (membershipId: string, settlementId: string, reason: string) =>
    request<{ settlementId: string; reversalPaymentId: string; amountMinor: number; currency: string }>(
      `/api/dentists/${membershipId}/settlements/${settlementId}/reverse`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  /** Pay a dentist an amount the clinic names, not the period total. */
  payDentist: (membershipId: string, body: Record<string, unknown>) =>
    request<{
      id: string;
      amountMinor: number;
      currency: string;
      kind: string;
      allocated: number;
      advanceMinor: number;
      remainingOutstandingMinor: number;
    }>(`/api/dentists/${membershipId}/pay`, { method: 'POST', body: JSON.stringify(body) }),

  postSalary: (membershipId: string, month: string) =>
    request<{ id: string; month: string; amountMinor: number; coveredDays: number; monthDays: number }>(
      `/api/dentists/${membershipId}/post-salary`,
      { method: 'POST', body: JSON.stringify({ month }) },
    ),

  dentistStatement: (membershipId: string) =>
    request<DentistStatement>(`/api/dentists/${membershipId}/statement`),

  createAgreement: (membershipId: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/dentists/${membershipId}/agreements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  settleCommission: (membershipId: string, body: Record<string, unknown>) =>
    request<{ id: string; amountMinor: number; currency: string; accruals: number }>(
      `/api/dentists/${membershipId}/settle`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ------------------------------------------------------------------ lab
  laboratories: () =>
    request<{
      laboratories: {
        id: string;
        name: string;
        contactName: string | null;
        phone: string | null;
        whatsappPhone: string | null;
        email: string | null;
        address: string | null;
        notes: string | null;
        /** false means retired: kept for its history, out of the pickers. */
        active: boolean;
        billedMinor: number;
        paidMinor: number;
        outstandingMinor: number;
        /** Sent to the lab and not yet back: not owed yet, but coming. */
        pendingMinor: number;
      }[];
    }>('/api/lab/laboratories'),

  createLaboratory: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/lab/laboratories', { method: 'POST', body: JSON.stringify(body) }),

  labOrders: (filters: { status?: string; patientId?: string } = {}) => {
    const q = new URLSearchParams();
    if (filters.status) q.set('status', filters.status);
    if (filters.patientId) q.set('patientId', filters.patientId);
    const query = q.toString();
    return request<{ orders: LabOrderRow[] }>(`/api/lab/orders${query ? `?${query}` : ''}`);
  },

  createLabOrder: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/lab/orders', { method: 'POST', body: JSON.stringify(body) }),

  setLabStatus: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; status: string; allowedNext: string[] }>(`/api/lab/orders/${id}/status`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  labWhatsApp: (id: string, body: Record<string, unknown>) =>
    request<{ draft: WhatsAppDraft }>(`/api/lab/orders/${id}/whatsapp`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ------------------------------------------------------------ inventory
  materials: () =>
    request<{ costingMethod: string; currency: string; materials: MaterialRow[] }>(
      '/api/inventory/materials',
    ),

  createMaterial: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/inventory/materials', { method: 'POST', body: JSON.stringify(body) }),

  recordMovement: (body: Record<string, unknown>) =>
    request<{ id: string; onHand: number; level: string; warning: string | null }>(
      '/api/inventory/movements',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  suppliers: () =>
    request<{
      suppliers: {
        id: string;
        name: string;
        contactName: string | null;
        phone: string | null;
        whatsappPhone: string | null;
        email: string | null;
        address: string | null;
        notes: string | null;
        /** false means retired: kept for its history, out of the pickers. */
        active: boolean;
        billedMinor: number;
        paidMinor: number;
        outstandingMinor: number;
      }[];
    }>('/api/inventory/suppliers'),

  createSupplier: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/inventory/suppliers', { method: 'POST', body: JSON.stringify(body) }),

  updateSupplier: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/inventory/suppliers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * The server decides between deleting and deactivating, depending on whether
   * anything references the record, and says which it did. The caller must not
   * assume — telling someone their supplier was deleted when it was retired is
   * how records go missing in people's heads.
   */
  deleteSupplier: (id: string) =>
    request<Removal>(`/api/inventory/suppliers/${id}`, { method: 'DELETE' }),

  updateMaterial: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/inventory/materials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteMaterial: (id: string) =>
    request<Removal>(`/api/inventory/materials/${id}`, { method: 'DELETE' }),

  postPurchase: (id: string) =>
    request<{ id: string; alreadyPosted: boolean }>(`/api/inventory/purchases/${id}/post`, { method: 'POST' }),

  receivePurchase: (id: string) =>
    request<{ id: string; alreadyReceived: boolean }>(`/api/inventory/purchases/${id}/receive`, {
      method: 'POST',
    }),

  deletePurchase: (id: string) =>
    request<{ deleted: boolean }>(`/api/inventory/purchases/${id}`, { method: 'DELETE' }),

  updateLaboratory: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/lab/laboratories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteLaboratory: (id: string) =>
    request<Removal>(`/api/lab/laboratories/${id}`, { method: 'DELETE' }),

  updateLabOrder: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/lab/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteLabOrder: (id: string) =>
    request<{ deleted: boolean }>(`/api/lab/orders/${id}`, { method: 'DELETE' }),

  updateInvoice: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; updated: boolean }>(`/api/finance/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteInvoice: (id: string) =>
    request<{ deleted: boolean }>(`/api/finance/invoices/${id}`, { method: 'DELETE' }),

  unbilled: () =>
    request<{
      totalMinor: number;
      count: number;
      patients: {
        patient: {
          id: string;
          patientNumber: string;
          givenName: string;
          familyName: string;
          givenNameAlt: string | null;
          familyNameAlt: string | null;
        };
        currency: string;
        totalMinor: number;
        items: {
          id: string;
          nameEn: string;
          nameAr: string;
          toothFdi: string | null;
          priceMinor: number;
          performedAt: string;
          dentistName: string | null;
        }[];
      }[];
    }>('/api/finance/unbilled'),

  payables: () => request<Payables>('/api/finance/payables'),

  voidPayment: (id: string, reason: string) =>
    request<{
      reversalId: string;
      /** Commission the dentist has ALREADY been paid, which they now owe back. */
      commissionAlreadySettledMinor: number;
      commissionReversedMinor: number;
      invoicesReopened: number;
    }>(`/api/finance/payments/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Allergies, current medicines and a note — the record the safety check reads. */
  updateMedicalHistory: (
    patientId: string,
    body: {
      allergyStatus?: 'not_recorded' | 'none_known' | 'positive';
      allergies?: { substance: string; reaction?: string | null; severity?: string }[];
      medications?: { name: string; ingredients?: string[] }[];
      freeText?: string | null;
    },
  ) =>
    request<{
      medicalHistory: { allergyStatus: string; reviewedAt: string | null; freeText: string | null; medications: { name: string; ingredients?: string[] }[] } | null;
      allergies: { id: string; substance: string; reaction: string | null; severity: string }[];
    }>(`/api/patients/${patientId}/medical-history`, { method: 'PATCH', body: JSON.stringify(body) }),

  archivePatient: (id: string, archived: boolean) =>
    request<{ patient: unknown }>(`/api/patients/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }),

  deletePatient: (id: string) => request<{ deleted: boolean }>(`/api/patients/${id}`, { method: 'DELETE' }),

  /* ------------------------------------------------------- import / export */

  exportCatalogue: () => request<{ exports: ExportOption[] }>('/api/exports/catalogue'),

  /**
   * Downloading a file, rather than reading JSON.
   *
   * Goes through `fetch` instead of pointing a link at the URL so an error
   * arrives as an error the page can show, rather than as a browser tab
   * containing a JSON blob. The blob is handed to a temporary anchor because
   * that is the only way to give a fetched file a filename.
   */
  downloadExport: async (
    kind: string,
    params: { from?: string; to?: string; dentistId?: string; includeArchived?: boolean } = {},
  ): Promise<{ filename: string; rows: number }> => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '' && v !== false) query.set(k, String(v));
    }
    const res = await fetch(`/api/exports/${kind}.xlsx?${query}`, { credentials: 'include' });
    if (!res.ok) {
      let message = 'That export could not be produced.';
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        /* keep the default */
      }
      throw new Error(message);
    }

    const disposition = res.headers.get('Content-Disposition') ?? '';
    const filename =
      /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${kind}.xlsx`;
    const rows = Number(res.headers.get('X-Oxy-Rows') ?? 0);

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Freed on the next tick: revoking immediately races the download in
    // some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);

    return { filename, rows };
  },

  downloadImportTemplate: async (): Promise<void> => {
    const res = await fetch('/api/patients/import/template', { credentials: 'include' });
    if (!res.ok) throw new Error('The template could not be produced.');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'oxy-flows-patient-import.xlsx';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },

  previewImport: (content: string, filename: string) =>
    request<ImportReport>('/api/patients/import/preview', {
      method: 'POST',
      body: JSON.stringify({ content, filename }),
    }),

  commitImport: (content: string, filename: string, acceptDuplicateLines: number[]) =>
    request<ImportReport & { imported: number; created: { line: number; patientNumber: string }[] }>(
      '/api/patients/import/commit',
      {
        method: 'POST',
        body: JSON.stringify({ content, filename, acceptDuplicateLines }),
      },
    ),

  purchases: () =>
    request<{
      purchases: {
        id: string;
        number: string;
        status: string;
        supplier: string;
        currency: string;
        invoicedOn: string;
        totalMinor: number;
        /** Paid against this bill specifically. */
        paidMinor: number;
        outstandingMinor: number;
        supplierId: string;
        received: boolean;
      }[];
    }>('/api/inventory/purchases'),

  recordPurchase: (body: Record<string, unknown>) =>
    request<{ id: string; action: string; payableMinor: number; currency: string }>(
      '/api/inventory/purchases',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  priceRequest: (body: Record<string, unknown>) =>
    request<{ draft: WhatsAppDraft; supplier: { id: string; name: string } }>(
      '/api/inventory/price-request',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  inventoryAlerts: () =>
    request<{
      lowStock: {
        id: string;
        sku: string;
        nameEn: string;
        nameAr: string;
        onHand: number;
        threshold: number;
        unit: string;
        level: string;
      }[];
      expiring: { batchId: string; lot: string; expiresOn: string; nameEn: string; nameAr: string }[];
    }>('/api/inventory/alerts'),

  // ---------------------------------------------------------------- clinic
  clinicSettings: () =>
    request<{
      clinic: {
        id: string;
        slug: string;
        nameEn: string;
        nameAr: string;
        countryCode: string | null;
        timezone: string;
        defaultLocale: string;
        defaultCurrency: string;
        descriptionEn: string | null;
        descriptionAr: string | null;
        phone: string | null;
        email: string | null;
        address: string | null;
        publicPagePublished: boolean;
        showTeamPublicly: boolean;
        publicBookingMode: string;
        /** A code at every sign-in for everyone in the clinic. */
        twoStepEverySignIn: boolean;
    /** What the clinic practises: 'dental', 'dermatology', 'aesthetics'. */
    specialties: string[];
      };
      chairs: { id: string; nameEn: string; nameAr: string; active: boolean }[];
      subscription: {
        plan: string;
        status: string;
        endsAt: string | null;
        maxDentists: number | null;
        maxActiveUsers: number | null;
        storageGb: number | null;
      } | null;
      usage: { storageBytes: number };
      devices: {
        id: string;
        name: string;
        platform: string;
        appVersion: string;
        lastSeenAt: string | null;
        revoked: boolean;
      }[];
    }>('/api/clinic/settings'),

  updateClinicSettings: (body: Record<string, unknown>) =>
    request<{ updated: string[] }>('/api/clinic/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  staff: () =>
    request<{
      staff: {
        membershipId: string;
        name: string;
        email: string;
        roles: string[];
        granted: string[];
        revoked: string[];
        status: string;
        isDentist: boolean;
        /** What patients are told this person does. Not a role. */
        publicTitle: string | null;
        effectivePermissions: string[];
      }[];
      roleTemplates: Record<string, string[]>;
      allPermissions: string[];
    }>('/api/clinic/staff'),

  updateStaff: (membershipId: string, body: Record<string, unknown>) =>
    request<{ membershipId: string; updated: string[] }>(`/api/clinic/staff/${membershipId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** Invite a colleague by email: a waiting membership that opens when they create their account or sign in. */
  inviteStaff: (body: { email: string; fullName: string; roles: string[]; isDentist: boolean; publicTitle?: string | null }) =>
    request<{ membershipId: string; email: string; invited: true; emailed: boolean }>('/api/clinic/staff/invite', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** The invitation email once more, for someone who has not joined yet. */
  resendInvite: (membershipId: string) =>
    request<{ membershipId: string; emailed: boolean }>(`/api/clinic/staff/${membershipId}/resend-invite`, { method: 'POST' }),

  createCatalogueItem: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/clinic/catalogue', { method: 'POST', body: JSON.stringify(body) }),

  createChair: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/clinic/chairs', { method: 'POST', body: JSON.stringify(body) }),

  auditLog: () =>
    request<{
      events: {
        id: string;
        action: string;
        entity: string | null;
        summary: string | null;
        actor: string | null;
        occurredAt: string;
        recordedAt: string;
      }[];
      note: string;
    }>('/api/clinic/audit'),

  appointmentWhatsApp: (id: string, body: Record<string, unknown>) =>
    request<{ draft: WhatsAppDraft }>(`/api/clinic/appointments/${id}/whatsapp`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---------------------------------------------------------------- public
  // ------------------------------------------------------------- platform
  platformOverview: (q?: string) =>
    request<PlatformOverview>(`/api/platform/overview${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  createPlatformClinic: (body: Record<string, unknown>) =>
    request<{
      clinicId: string;
      userId: string;
      slug: string;
      plan: string;
      /** The clinic's licence code, issued with the clinic and kept visible on its page. */
      activationCode: { code: string; email: string; emailed: boolean };
    }>('/api/platform/clinics', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  platformClinic: (clinicId: string) => request<PlatformClinicDetail>(`/api/platform/clinics/${clinicId}`),

  /** Hold access for `days` days (0 resumes). Nothing is touched. */
  pauseClinic: (clinicId: string, days: number, reason?: string) =>
    request<{ clinicId: string; paused: boolean; days: number; emailed: number }>(`/api/platform/clinics/${clinicId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ days, ...(reason ? { reason } : {}) }),
    }),

  /** The whole clinic as a file, fetched as a blob for download. */
  exportClinic: async (clinicId: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(`/api/platform/clinics/${clinicId}/export`, { credentials: 'include' });
    if (!res.ok) throw new ApiError(res.status, null, `Export failed (${res.status})`);
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'clinic.oxyclinic.json';
    return { blob: await res.blob(), filename: name };
  },

  purgeClinic: (clinicId: string, confirmSlug: string, reason: string) =>
    request<{ clinicId: string; removedRows: number }>(`/api/platform/clinics/${clinicId}/purge`, {
      method: 'POST',
      body: JSON.stringify({ confirmSlug, reason }),
    }),

  importClinic: (archive: unknown) =>
    request<{ clinicId: string; slug: string; inserted: Record<string, number>; files: number }>('/api/platform/clinics/import', {
      method: 'POST',
      body: JSON.stringify(archive),
    }),

  /** A fresh code for a clinic's owner; earlier unused ones stop working. */
  reissueOwnerCode: (clinicId: string, validDays = 14) =>
    request<{ id: string; code: string; shownOnce: true; email: string; validDays: number; ownerStatus: string; emailed: boolean }>(
      `/api/platform/clinics/${clinicId}/activation-code`,
      { method: 'POST', body: JSON.stringify({ validDays }) },
    ),

  /** The clinic's current code to its owner by email, unchanged. */
  emailOwnerCode: (clinicId: string) =>
    request<{ emailed: boolean; email: string }>(`/api/platform/clinics/${clinicId}/activation-code/email`, { method: 'POST' }),

  setClinicStatus: (clinicId: string, body: Record<string, unknown>) =>
    request<{ clinicId: string; status: string }>(`/api/platform/clinics/${clinicId}/status`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  setSubscription: (clinicId: string, body: Record<string, unknown>) =>
    request<{ clinicId: string; plan: string; months: number }>(
      `/api/platform/clinics/${clinicId}/subscription`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  activationCodes: () => request<{ codes: ActivationCode[] }>('/api/platform/activation-codes'),

  /** The full code comes back once and is never retrievable again. */
  issueActivationCode: (body: Record<string, unknown>) =>
    request<{ id: string; code: string; shownOnce: boolean; email: string; plan: string }>(
      '/api/platform/activation-codes',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  revokeActivationCode: (id: string) =>
    request<{ id: string; revoked: boolean }>(`/api/platform/activation-codes/${id}/revoke`, {
      method: 'POST',
    }),

  supportAccess: () => request<{ grants: SupportGrant[] }>('/api/platform/support-access'),

  grantSupportAccess: (body: Record<string, unknown>) =>
    request<{ id: string; expiresInHours: number }>('/api/platform/support-access', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  endSupportAccess: (id: string) =>
    request<{ id: string; ended: boolean }>(`/api/platform/support-access/${id}/end`, { method: 'POST' }),

  platformOperators: () =>
    request<{
      operators: { id: string; email: string; name: string; status: string; hasPassword: boolean; isYou: boolean }[];
    }>('/api/platform/operators'),

  setPlatformOperator: (body: Record<string, unknown>) =>
    request<{ email: string; isOperator: boolean }>('/api/platform/operators', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  publicClinic: (slug: string) =>
    request<{
      clinic: {
        slug: string;
        nameEn: string;
        nameAr: string;
        descriptionEn: string | null;
        descriptionAr: string | null;
        phone: string | null;
        email: string | null;
        address: string | null;
        mapUrl: string | null;
        social: Record<string, string>;
        openingHours: { weekday: number; opens: string; closes: string }[];
        timezone: string;
        bookingMode: string;
      };
      services: { id: string; nameEn: string; nameAr: string; category: string; durationMinutes: number }[];
      team: { membershipId: string; name: string; title: string | null }[];
      /** False when the clinic has not published its team; slots stay anonymous. */
      canChooseDentist: boolean;
    }>(`/api/public/clinics/${slug}`),

  publicAvailability: (slug: string, date: string) =>
    request<{
      bookingDisabled: boolean;
      canChooseDentist?: boolean;
      timezone?: string;
      dentists?: { membershipId: string; name: string }[];
      slots: { startsAt: string; membershipId: string; dentistName: string | null }[];
    }>(`/api/public/clinics/${slug}/availability/${date}`),

  publicBook: (slug: string, body: Record<string, unknown>) =>
    request<{ status: string; message: string }>(`/api/public/clinics/${slug}/book`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ------------------------------------------------- patient sources (2026-10-21)
  patientSources: (all = false) => request<{ sources: PatientSource[] }>(`/api/patient-sources${all ? '?all=1' : ''}`),
  createPatientSource: (body: { nameEn: string; nameAr?: string | null }) =>
    request<{ source: PatientSource }>('/api/patient-sources', { method: 'POST', body: JSON.stringify(body) }),
  updatePatientSource: (id: string, body: Partial<{ nameEn: string; nameAr: string | null; active: boolean; sort: number }>) =>
    request<{ source: PatientSource }>(`/api/patient-sources/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removePatientSource: (id: string) => request<{ removed: boolean; retired: boolean }>(`/api/patient-sources/${id}`, { method: 'DELETE' }),

  // ------------------------------------------------------ the patient's visits
  journey: (patientId: string) => request<Journey>(`/api/clinical/visits/${patientId}`),
  setNoteVisibility: (id: string, visibility: 'private' | 'shared') =>
    request<{ visibility: string }>(`/api/clinical/notes/${id}/visibility`, { method: 'PATCH', body: JSON.stringify({ visibility }) }),

  // ------------------------------------------------- missed appointments
  missedAppointments: (q: { open?: boolean; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams({ ...(q.open ? { open: '1' } : {}), ...(q.from ? { from: q.from } : {}), ...(q.to ? { to: q.to } : {}) }).toString();
    return request<{ missed: MissedAppointment[]; open: number }>(`/api/appointments/missed${qs ? `?${qs}` : ''}`);
  },
  followUpAppointment: (id: string, body: { status: 'contacted' | 'rebooked' | 'closed' | null; note?: string }) =>
    request<{ ok: true }>(`/api/appointments/${id}/followup`, { method: 'POST', body: JSON.stringify(body) }),

  // ------------------------------------------------------------- reports
  reportOverview: (p: ReportPeriod) =>
    request<{
      scope: 'clinic' | 'own';
      newPatients: number;
      patientsSeen: number;
      appointmentsCompleted: number;
      noShows: number;
      noShowRate: number | null;
      cancelled: number;
      proceduresCompleted: number;
    }>(`/api/reports/overview?${periodQs(p)}`),
  reportPatients: (p: ReportPeriod) =>
    request<{
      scope: 'clinic' | 'own';
      total: number;
      bySource: { sourceId: string | null; nameEn: string | null; nameAr: string | null; count: number }[];
      byReferrer: { membershipId: string; name: string | null; count: number }[];
      patients: {
        id: string;
        patientNumber: string;
        givenName: string;
        familyName: string;
        givenNameAlt: string | null;
        familyNameAlt: string | null;
        createdAt: string;
        source: { en: string | null; ar: string | null } | null;
        sourceDetail: string | null;
        referredBy: string | null;
      }[];
    }>(`/api/reports/patients?${periodQs(p)}`),
  reportDentists: (p: ReportPeriod) =>
    request<{
      scope: 'clinic' | 'own';
      currency: string | null;
      dentists: {
        membershipId: string;
        name: string;
        title: string | null;
        patientsSeen: number;
        appointmentsCompleted: number;
        noShows: number;
        noShowRate: number | null;
        cancelled: number;
        patientsReferred: number;
        proceduresCompleted: number;
        proceduresValueMinor?: number;
      }[];
    }>(`/api/reports/dentists?${periodQs(p)}`),
  reportProcedures: (p: ReportPeriod) =>
    request<{ scope: 'clinic' | 'own'; procedures: { nameEn: string; nameAr: string | null; code: string | null; count: number; patients: number; valueMinor?: number }[] }>(
      `/api/reports/procedures?${periodQs(p)}`,
    ),
  reportMissed: (p: ReportPeriod) => request<{ scope: 'clinic' | 'own'; missed: MissedAppointment[] }>(`/api/reports/missed?${periodQs(p)}`),
};
